import { useSearchParams } from 'react-router-dom';
import { BotMessageSquare, Loader2 } from 'lucide-react';
import { McpScope } from '@masters-club/types';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { useTenant } from '@/providers/tenant-context';
import { hostOf } from '../format';
import { TenantChoice } from './tenant-choice';
import { useMcpConsent } from './use-consent';

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-2 border-b py-2 last:border-b-0">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-sm font-medium break-all">{children}</span>
    </div>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-background to-muted p-4">
      <Card className="w-full max-w-lg shadow-2xl">{children}</Card>
    </div>
  );
}

/**
 * Страница согласия OAuth для MCP-клиента: /mcp/consent?request=<id>.
 * Маршрут под <Authenticated>: без входа — редирект на /login?to=<этот URL>.
 */
export function McpConsentPage() {
  const [searchParams] = useSearchParams();
  const requestId = searchParams.get('request');
  const { request, decision, allowWrite, setAllowWrite, tenant, setTenant } = useMcpConsent(requestId);
  const { tenants } = useTenant();

  if (!requestId) {
    return (
      <Shell>
        <CardContent className="py-10 text-center text-sm text-destructive">
          В ссылке нет идентификатора запроса. Начните подключение в ИИ-клиенте заново.
        </CardContent>
      </Shell>
    );
  }

  if (request.isLoading) {
    return (
      <Shell>
        <CardContent className="flex justify-center py-10">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </CardContent>
      </Shell>
    );
  }

  if (request.error || !request.data) {
    return (
      <Shell>
        <CardHeader>
          <CardTitle>Запрос недоступен</CardTitle>
          <CardDescription>
            {request.error?.message ?? 'Запрос авторизации не найден или истёк — начните подключение заново'}
          </CardDescription>
        </CardHeader>
      </Shell>
    );
  }

  const data = request.data;
  const wantsWrite = data.scopes.includes(McpScope.WRITE);
  const canChooseTenant = tenants.length > 1 && tenant !== null;
  const busy = decision.isPending;

  return (
    <Shell>
      <CardHeader>
        <div className="mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
          <BotMessageSquare className="h-6 w-6" />
        </div>
        <CardTitle>Разрешить доступ ИИ-клиенту?</CardTitle>
        <CardDescription>
          «{data.clientName}» получит доступ к админке от вашего имени — только к операциям, разрешённым вашей роли.
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-4">
        <div>
          <Row label="Клиент">{data.clientName}</Row>
          <Row label="Вернёт на">{hostOf(data.redirectUri)}</Row>
          {canChooseTenant ? null : <Row label="Площадка">{data.tenantSlug}</Row>}
          <Row label="Истекает запрос">{new Date(data.expiresAt).toLocaleTimeString('ru-RU')}</Row>
        </div>

        {canChooseTenant ? (
          <TenantChoice tenants={tenants} value={tenant} onChange={setTenant} disabled={busy} />
        ) : null}

        <div className="space-y-2 rounded-md border p-3">
          <p className="text-sm font-medium">Запрошенный доступ</p>
          <p className="text-sm text-muted-foreground">Чтение данных — всегда.</p>
          {wantsWrite ? (
            <div className="flex items-start gap-2">
              <Checkbox
                id="mcp-consent-write"
                checked={allowWrite}
                onCheckedChange={(checked) => setAllowWrite(checked === true)}
                disabled={busy}
              />
              <Label htmlFor="mcp-consent-write" className="text-sm font-normal leading-snug">
                Разрешить запись — создание, изменение и удаление данных в пределах прав роли
              </Label>
            </div>
          ) : null}
        </div>

        {decision.error ? (
          <Alert variant="destructive">
            <AlertDescription>{decision.error.message}</AlertDescription>
          </Alert>
        ) : null}
      </CardContent>

      <CardFooter className="flex justify-end gap-2">
        <Button
          variant="outline"
          disabled={busy}
          onClick={() => decision.mutate({ approve: false, allowWrite: false })}
        >
          Отклонить
        </Button>
        <Button disabled={busy} onClick={() => decision.mutate({ approve: true, allowWrite })}>
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          Разрешить
        </Button>
      </CardFooter>
    </Shell>
  );
}
